import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, getAuthToken } from "../lib/apiClient";
import { toast } from "sonner";
import NPSModal from "./NPSModal";
import { N1DownloadBlock } from "./CaseDownloadPanel/N1DownloadBlock";
import { N2DownloadBlock } from "./CaseDownloadPanel/N2DownloadBlock";
import { N2CtaBlock } from "./CaseDownloadPanel/N2CtaBlock";
import { ContactRequestModal } from "./CaseDownloadPanel/ContactRequestModal";

const N1_DOC_TIPOS = ["diagnostico_n1_zip", "diagnostico_n1_pdf"];
const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Panel reutilizable para el dueño del caso:
 * - Descarga del PDF Diagnóstico (cuando documents.approved_for_download === true).
 * - CTA "Solicitar Kit Documental" cuando state ∈ {nivel2_offer_sent, nivel2_payment_pending}.
 *   - nivel2_offer_sent → "Solicitar Kit Documental" (lleva a /cases/{id}/nivel2-request)
 *   - nivel2_payment_pending → "Continuar pago Kit Documental" (lleva a /cases/{id}/payment-n2)
 * - Banner informativo: pago de Kit Documental placeholder.
 *
 * Props:
 *   caseDoc: objeto caso (con id, state).
 *   onCaseChanged: callback opcional cuando el caso cambia.
 */
export default function CaseDownloadPanel({ caseDoc, onCaseChanged }) {
  const navigate = useNavigate();
  const [docs, setDocs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [contactReq, setContactReq] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [npsResponses, setNpsResponses] = useState(null);
  const [showNPSModal, setShowNPSModal] = useState(false);
  const [npsLevel, setNpsLevel] = useState("nivel1");

  const loadDocs = useCallback(async () => {
    if (!caseDoc?.id) return;
    try {
      const { data } = await api.get(`/cases/${caseDoc.id}/documents`);
      setDocs(data.items || []);
    } catch (e) {
      // 403 / 404 ok aquí
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [caseDoc?.id]);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  // Cargar respuestas NPS existentes
  useEffect(() => {
    if (!caseDoc?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/cases/${caseDoc.id}/nps`);
        if (!cancelled) setNpsResponses(data);
      } catch (err) {
        // 404 esperado si user no respondió NPS aún. No log en prod.
        if (process.env.NODE_ENV === "development" && err?.response?.status !== 404) {
          console.warn("NPS responses load failed:", err);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [caseDoc?.id]);

  // Cargar estado de la solicitud de contacto comercial Kit Documental si aplica.
  useEffect(() => {
    if (!caseDoc?.id) return;
    const validStates = new Set(["nivel1_delivered", "nivel2_offer_sent", "nivel2_payment_pending"]);
    if (!validStates.has(caseDoc.state)) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/cases/${caseDoc.id}/contact-commercial-n2`);
        if (!cancelled) setContactReq(data.request);
      } catch (err) {
        // 404 esperado si no hay request previo. No log en prod.
        if (process.env.NODE_ENV === "development" && err?.response?.status !== 404) {
          console.warn("Contact N2 request load failed:", err);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [caseDoc?.id, caseDoc?.state]);

  const handleRequestContact = async () => {
    setActing(true);
    try {
      const { data } = await api.post(`/cases/${caseDoc.id}/contact-commercial-n2`);
      setContactReq(data.request);
      setShowContactModal(false);
      toast.success("Solicitud enviada. Un asesor te contactará en 1-2 días hábiles.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo enviar la solicitud");
    } finally {
      setActing(false);
    }
  };

  const n1Doc = docs?.find(
    (d) => N1_DOC_TIPOS.includes(d.tipo) && d.approved_for_download,
  );
  const n2Doc = docs?.find(
    (d) => d.tipo === "paquete_n2_zip" && d.approved_for_download,
  );

  const handleDownload = async () => {
    if (!n1Doc) return;
    setActing(true);
    try {
      const url = `${REACT_APP_BACKEND_URL}/api/cases/${caseDoc.id}/documents/${n1Doc.id}/download`;
      // Auth dual: cookie automática (credentials: include) + Bearer fallback
      const token = getAuthToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const resp = await fetch(url, { credentials: "include", headers });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(`HTTP ${resp.status}: ${txt.slice(0, 120)}`);
      }
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = n1Doc.filename || `diagnostico_n1_${caseDoc.id.slice(0, 8)}.${n1Doc.tipo === "diagnostico_n1_zip" ? "zip" : "pdf"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
      toast.success("Descarga iniciada");
      
      // Fase 11.1.A: Mostrar modal NPS post-descarga si no ha respondido aún
      setTimeout(() => {
        const hasRespondedNivel1 = npsResponses?.nivel1 !== null && npsResponses?.nivel1 !== undefined;
        if (!hasRespondedNivel1) {
          setNpsLevel("nivel1");
          setShowNPSModal(true);
        }
      }, 1500);
    } catch (e) {
      toast.error(e.message || "No se pudo descargar");
    } finally {
      setActing(false);
    }
  };

  const handleAcceptN2 = () => {
    // Fase 11.2: navegar a la página de solicitud N2 (intake + pago).
    navigate(`/cases/${caseDoc.id}/nivel2-request`);
  };

  const handleResumeN2Payment = () => {
    navigate(`/cases/${caseDoc.id}/payment-n2`);
  };

  const handleDownloadN2 = async () => {
    if (!n2Doc) return;
    setActing(true);
    try {
      const url = `${REACT_APP_BACKEND_URL}/api/cases/${caseDoc.id}/documents/${n2Doc.id}/download`;
      const token = getAuthToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const resp = await fetch(url, { credentials: "include", headers });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(`HTTP ${resp.status}: ${txt.slice(0, 200)}`);
      }
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = n2Doc.filename || `kit-documental-n2-${caseDoc.id.slice(0, 8)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
      toast.success("Descarga del Kit Documental iniciada");
      // Trigger NPS nivel2 si no respondió
      setTimeout(() => {
        const hasRespondedNivel2 = npsResponses?.nivel2 !== null && npsResponses?.nivel2 !== undefined;
        if (!hasRespondedNivel2) {
          setNpsLevel("nivel2");
          setShowNPSModal(true);
        }
      }, 1500);
    } catch (e) {
      toast.error(e.message || "No se pudo descargar el Kit Documental");
    } finally {
      setActing(false);
    }
  };

  if (loading) {
    return null;
  }

  const state = caseDoc?.state;
  // Estados en que la descarga es válida
  const downloadStates = new Set([
    "nivel1_delivered",
    "nivel2_offer_sent",
    "nivel2_payment_pending",
    "nivel2_paid",
    "nivel2_processing",
    "nivel2_needs_human_review",
    "nivel2_revision_requested",
    "nivel2_delivered",
    "closed",
  ]);
  const n2DownloadStates = new Set(["nivel2_delivered", "closed"]);

  // nivel2_offer_sent se presenta con N2OfferCard (dentro del diagnóstico).
  const showN2Cta = state === "nivel2_payment_pending";
  if (!n1Doc && !n2Doc && !showN2Cta) return null;

  return (
    <div className="mt-6 space-y-4" data-testid="case-download-panel">
      {n1Doc && downloadStates.has(state) && (
        <N1DownloadBlock
          n1Doc={n1Doc}
          caseDoc={caseDoc}
          acting={acting}
          onDownload={handleDownload}
        />
      )}

      {n2Doc && n2DownloadStates.has(state) && (
        <N2DownloadBlock
          n2Doc={n2Doc}
          caseDoc={caseDoc}
          acting={acting}
          onDownload={handleDownloadN2}
        />
      )}

      {showN2Cta && (
        <N2CtaBlock
          state={state}
          acting={acting}
          onAcceptN2={handleAcceptN2}
          onResumeN2Payment={handleResumeN2Payment}
          onEditN2Intake={handleAcceptN2}
          contactReq={contactReq}
          onRequestContact={() => setShowContactModal(true)}
        />
      )}

      <ContactRequestModal
        show={showContactModal}
        onClose={() => setShowContactModal(false)}
        caseDoc={caseDoc}
        acting={acting}
        onConfirm={handleRequestContact}
      />

      {state === "nivel2_payment_pending" && (
        <div className="border border-amber-300 bg-amber-50 p-6" data-testid="n2-payment-placeholder">
          <p className="text-[11px] uppercase tracking-widest text-amber-900 font-medium mb-2">
            Pago Kit Documental — Próximamente
          </p>
          <p className="text-sm text-amber-900">
            El flujo de pago de Kit Documental estará habilitado en la siguiente fase. Por ahora contáctanos por
            correo para coordinar el pago manual y arrancar la generación del paquete documental.
          </p>
        </div>
      )}

      {/* Modal NPS (Fase 11.1.A) */}
      <NPSModal
        open={showNPSModal}
        onClose={() => setShowNPSModal(false)}
        caseId={caseDoc?.id}
        nivel={npsLevel}
        onSubmitted={() => {
          // Recargar respuestas NPS
          api.get(`/cases/${caseDoc.id}/nps`)
            .then(({ data }) => setNpsResponses(data))
            .catch(() => {});
        }}
      />
    </div>
  );
}
